/**
 * ตัวอย่างการแสดงผลของ widget template
 *
 * วาดด้วย React ไม่ใช่ดึงรูปจาก previewUrl ที่ API ส่งมา เพราะไฟล์
 * /w/v1/previews/{code}.svg ไม่มีอยู่จริง — Hosting จะ fallback เป็น index.html
 * แล้วตอบ 200 ทำให้ <img> รูปแตกแบบเงียบ ๆ หาสาเหตุยาก
 *
 * ข้อดีของการวาดเอง: คมทุกความละเอียดจอ, เปลี่ยนตามธีม light/dark ของหลังบ้าน
 * ด้วย CSS variable ชุดเดียวกับหน้าอื่น และไม่มีคำขอไฟล์เพิ่ม
 *
 * นี่คือ "โครงร่าง" ของ layout ไม่ใช่ของจริงจาก DB — จงใจใช้บล็อกสีเทาแทนรูป
 * และข้อความตัวอย่าง เพื่อให้ผู้ใช้โฟกัสที่ "การจัดวาง" ซึ่งเป็นสิ่งที่กำลังเลือก
 */
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { Modal, Badge, Button } from '../../components/ui/index.js';
import type { TemplateInfo } from '../../lib/queries.js';

const SAMPLE = [
  'เปิดตัวระบบจัดการบทความรุ่นใหม่',
  'สรุปเทรนด์เทคโนโลยีองค์กรปี 2026',
  'วิธีเลือก CMS ให้เหมาะกับทีมเล็ก',
  'กรณีศึกษา: ลดเวลาทำงานเอกสารลง 40%',
];

/** เส้นข้อความจำลอง — w คือความกว้างเป็น % ของคอลัมน์ */
function Line({ w = 100, h = 6, dim = false }: { w?: number; h?: number; dim?: boolean }) {
  return (
    <div
      className="rounded-full"
      style={{
        width: `${w}%`,
        height: h,
        background: dim ? 'var(--line)' : 'var(--line-strong, var(--line))',
        opacity: dim ? 0.7 : 1,
      }}
    />
  );
}

/** บล็อกแทนรูปภาพ — มีไอคอนภูเขาจาง ๆ ให้รู้ว่าเป็นรูป ไม่ใช่กล่องว่าง */
function Thumb({ className = '', style }: { className?: string; style?: CSSProperties }) {
  return (
    <div
      className={`relative overflow-hidden rounded-md ${className}`}
      style={{ background: 'var(--sunken)', ...style }}
    >
      <svg viewBox="0 0 24 24" className="absolute inset-0 m-auto h-1/3 w-1/3 opacity-25"
           fill="none" stroke="var(--ink-3)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <circle cx="8.5" cy="9.5" r="1.6" />
        <path d="M21 16l-5-5-6 6-3-3-4 4" />
      </svg>
    </div>
  );
}

function Card({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return (
    <div
      className={`rounded-lg border p-2 ${className}`}
      style={{ borderColor: 'var(--line)', background: 'var(--surface, transparent)' }}
    >
      {children}
    </div>
  );
}

const Title = ({ text }: { text: string }) => (
  <p className="truncate text-[11px] font-semibold leading-tight text-[var(--ink)]">{text}</p>
);

const Meta = () => (
  <div className="flex items-center gap-1.5">
    <Line w={22} h={4} dim />
    <Line w={14} h={4} dim />
  </div>
);

function CardGrid() {
  return (
    <div className="grid grid-cols-3 gap-2.5">
      {SAMPLE.slice(0, 3).map((t) => (
        <Card key={t} className="space-y-1.5">
          <Thumb style={{ aspectRatio: '16 / 9' }} />
          <Title text={t} />
          <Meta />
        </Card>
      ))}
    </div>
  );
}

function ListCompact() {
  return (
    <div className="space-y-2">
      {SAMPLE.map((t) => (
        <div key={t} className="flex items-center gap-2.5">
          <Thumb className="h-10 w-14 shrink-0" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <Title text={t} />
            <Meta />
          </div>
        </div>
      ))}
    </div>
  );
}

function Slider() {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Arrow dir="left" />
        <div className="grid flex-1 grid-cols-2 gap-2.5">
          {SAMPLE.slice(0, 2).map((t) => (
            <Card key={t} className="space-y-1.5">
              <Thumb style={{ aspectRatio: '16 / 9' }} />
              <Title text={t} />
              <Meta />
            </Card>
          ))}
        </div>
        <Arrow dir="right" />
      </div>
      <div className="flex justify-center gap-1.5">
        {[0, 1, 2].map((i) => (
          <span key={i} className="h-1.5 w-1.5 rounded-full"
                style={{ background: i === 0 ? 'var(--brand)' : 'var(--line)' }} />
        ))}
      </div>
    </div>
  );
}

function Arrow({ dir }: { dir: 'left' | 'right' }) {
  return (
    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border"
         style={{ borderColor: 'var(--line)' }}>
      <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="var(--ink-3)"
           strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
           style={{ transform: dir === 'left' ? 'rotate(180deg)' : undefined }}>
        <path d="M9 6l6 6-6 6" />
      </svg>
    </div>
  );
}

function MinimalText() {
  return (
    <div className="space-y-2.5">
      {SAMPLE.map((t, i) => (
        <div key={t} className="space-y-1.5 pb-2.5"
             style={{ borderBottom: i < SAMPLE.length - 1 ? '1px solid var(--line)' : undefined }}>
          <Title text={t} />
          <Meta />
        </div>
      ))}
    </div>
  );
}

function HeroFeature() {
  return (
    <div className="space-y-2.5">
      <Card className="space-y-1.5">
        <Thumb style={{ aspectRatio: '21 / 9' }} />
        <p className="truncate text-xs font-bold leading-tight text-[var(--ink)]">{SAMPLE[0]}</p>
        <Line w={80} h={5} dim />
        <Meta />
      </Card>
      <div className="grid grid-cols-3 gap-2">
        {SAMPLE.slice(1, 4).map((t) => (
          <div key={t} className="space-y-1">
            <Title text={t} />
            <Line w={45} h={4} dim />
          </div>
        ))}
      </div>
    </div>
  );
}

function Masonry() {
  // ความสูงต่างกันคือหัวใจของ masonry — ไล่เป็นคอลัมน์เพื่อให้เห็นการเหลื่อม
  const cols: { t: string; h: number }[][] = [
    [{ t: SAMPLE[0]!, h: 56 }, { t: SAMPLE[3]!, h: 34 }],
    [{ t: SAMPLE[1]!, h: 34 }, { t: SAMPLE[2]!, h: 56 }],
    [{ t: SAMPLE[2]!, h: 44 }, { t: SAMPLE[0]!, h: 44 }],
  ];
  return (
    <div className="grid grid-cols-3 gap-2.5">
      {cols.map((col, i) => (
        <div key={i} className="space-y-2.5">
          {col.map((item, j) => (
            <Card key={j} className="space-y-1.5">
              <Thumb style={{ height: item.h }} />
              <Title text={item.t} />
            </Card>
          ))}
        </div>
      ))}
    </div>
  );
}

function Ticker() {
  return (
    <div className="flex items-center gap-2 rounded-lg border px-2.5 py-2"
         style={{ borderColor: 'var(--line)' }}>
      <span className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold text-white"
            style={{ background: 'var(--brand)' }}>ล่าสุด</span>
      <div className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
        {SAMPLE.slice(0, 3).map((t, i) => (
          <span key={i} className="flex shrink-0 items-center gap-3 text-[11px] text-[var(--ink)]">
            {t}
            {i < 2 && <span className="h-1 w-1 rounded-full" style={{ background: 'var(--line)' }} />}
          </span>
        ))}
      </div>
      <svg viewBox="0 0 24 24" className="h-3 w-3 shrink-0" fill="none" stroke="var(--ink-3)"
           strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
    </div>
  );
}

function Timeline() {
  return (
    <div className="relative space-y-3 ps-4">
      <span className="absolute bottom-1 left-[3px] top-1 w-px" style={{ background: 'var(--line)' }} />
      {SAMPLE.map((t, i) => (
        <div key={t} className="relative space-y-1">
          <span
            className="absolute -start-4 top-1 h-[7px] w-[7px] rounded-full ring-2"
            style={{
              background: i === 0 ? 'var(--brand)' : 'var(--line)',
              '--tw-ring-color': 'var(--bg, #fff)',
            } as CSSProperties}
          />
          <Line w={18} h={4} dim />
          <Title text={t} />
        </div>
      ))}
    </div>
  );
}

/* ---- ชุดที่เพิ่มทีหลัง ---- */

/** กล่องรูปที่มีข้อความทับ + ม่านไล่สี — ใช้ร่วมกันใน bento กับ magazine */
function OverlayBox({ text, h, big = false }: { text: string; h: number; big?: boolean }) {
  return (
    <div className="relative overflow-hidden rounded-lg" style={{ height: h, background: 'var(--sunken)' }}>
      <svg viewBox="0 0 24 24" className="absolute inset-0 m-auto h-1/4 w-1/4 opacity-20"
           fill="none" stroke="var(--ink-3)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <circle cx="8.5" cy="9.5" r="1.6" />
        <path d="M21 16l-5-5-6 6-3-3-4 4" />
      </svg>
      {/* ม่านไล่สีทึบด้านล่าง เลียนแบบของจริงที่ทำให้ตัวอักษรอ่านออกบนรูป */}
      <div className="absolute inset-0"
           style={{ background: 'linear-gradient(to top, rgba(0,0,0,.82), rgba(0,0,0,.35) 45%, transparent)' }} />
      <div className="absolute inset-x-0 bottom-0 space-y-1 p-2">
        <span className="inline-block rounded px-1 py-px text-[8px] font-bold text-white"
              style={{ background: 'var(--brand)' }}>หมวดหมู่</span>
        <p className={`truncate font-bold leading-tight text-white ${big ? 'text-[13px]' : 'text-[10px]'}`}>{text}</p>
      </div>
    </div>
  );
}

function Bento() {
  return (
    <div className="grid grid-cols-6 gap-2.5">
      <div className="col-span-4"><OverlayBox text={SAMPLE[0]!} h={112} big /></div>
      <div className="col-span-2 space-y-2.5">
        <OverlayBox text={SAMPLE[1]!} h={51} />
        <OverlayBox text={SAMPLE[2]!} h={51} />
      </div>
      <div className="col-span-3"><OverlayBox text={SAMPLE[3]!} h={58} /></div>
      <div className="col-span-3"><OverlayBox text={SAMPLE[1]!} h={58} /></div>
    </div>
  );
}

function MagazineMock() {
  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: '1.65fr 1fr' }}>
      <OverlayBox text={SAMPLE[0]!} h={150} big />
      <div className="flex flex-col">
        {SAMPLE.slice(1).map((t, i) => (
          <div key={t} className="flex items-center gap-2 py-2"
               style={{ borderBottom: i < 2 ? '1px solid var(--line)' : undefined }}>
            <span className="text-[11px] font-extrabold leading-none"
                  style={{ color: 'var(--brand)', opacity: 0.5 }}>{String(i + 2).padStart(2, '0')}</span>
            <div className="min-w-0 flex-1 space-y-1">
              <Title text={t} />
              <Line w={40} h={4} dim />
            </div>
            <Thumb className="h-8 w-11 shrink-0" />
          </div>
        ))}
      </div>
    </div>
  );
}

function Spotlight() {
  return (
    <div className="grid grid-cols-3 gap-2.5">
      {SAMPLE.slice(0, 3).map((t, i) => (
        <div
          key={t}
          className="relative overflow-hidden rounded-lg border p-2"
          style={{
            borderColor: i === 1 ? 'color-mix(in srgb, var(--brand) 45%, var(--line))' : 'var(--line)',
            transform: i === 1 ? 'translateY(-3px)' : undefined,
          }}
        >
          {/* ใบกลางจำลองตอนเมาส์ชี้ — แสงวงกลมตามตำแหน่งเคอร์เซอร์ */}
          {i === 1 && (
            <div className="pointer-events-none absolute inset-0"
                 style={{ background: 'radial-gradient(70px circle at 65% 30%, color-mix(in srgb, var(--brand) 22%, transparent), transparent 70%)' }} />
          )}
          <div className="relative space-y-1.5">
            <Thumb style={{ aspectRatio: '16 / 9' }} />
            <Title text={t} />
            <Meta />
            <p className="text-[9px] font-semibold"
               style={{ color: i === 1 ? 'var(--brand)' : 'var(--ink-3)' }}>อ่านต่อ →</p>
          </div>
        </div>
      ))}
    </div>
  );
}

const MOCKS: Record<string, () => ReactElement> = {
  'card-grid': CardGrid,
  'list-compact': ListCompact,
  slider: Slider,
  'minimal-text': MinimalText,
  'hero-feature': HeroFeature,
  masonry: Masonry,
  ticker: Ticker,
  timeline: Timeline,
  bento: Bento,
  magazine: MagazineMock,
  spotlight: Spotlight,
};

export function TemplateMock({ code }: { code: string }) {
  const Mock = MOCKS[code];
  if (!Mock) {
    return (
      <p className="py-8 text-center text-sm text-[var(--ink-3)]">
        ยังไม่มีตัวอย่างของรูปแบบนี้
      </p>
    );
  }
  return <Mock />;
}

export function TemplatePreviewModal({
  template, onClose, onSelect,
}: {
  template: TemplateInfo | null;
  onClose: () => void;
  onSelect?: (code: string) => void;
}) {
  return (
    <Modal
      open={Boolean(template)}
      onClose={onClose}
      title={template ? `ตัวอย่าง — ${template.name}` : ''}
      size="lg"
      footer={
        template && onSelect && template.available ? (
          <Button onClick={() => { onSelect(template.code); onClose(); }}>
            เลือกรูปแบบนี้
          </Button>
        ) : undefined
      }
    >
      {template && (
        <div className="space-y-3">
          {template.comingSoon ? (
            <Badge tone="gray">ยังไม่เปิดให้ใช้งาน — อยู่ในแผน Phase 2</Badge>
          ) : !template.available ? (
            <Badge tone="amber">ต้องใช้แพ็กเกจ {template.minPlan} ขึ้นไป</Badge>
          ) : null}
          {template.description && (
            <p className="text-sm text-[var(--ink-2)]">{template.description}</p>
          )}
          {/* กรอบจำลองพื้นที่บนเว็บลูกค้า เพื่อให้เห็นว่า widget กินพื้นที่แค่ไหน */}
          <div className="rounded-xl border p-4" style={{ borderColor: 'var(--line)', background: 'var(--sunken)' }}>
            <div className="rounded-lg p-3" style={{ background: 'var(--bg, #fff)' }}>
              <TemplateMock code={template.code} />
            </div>
          </div>
          <p className="text-xs text-[var(--ink-3)]">
            ภาพนี้แสดงการจัดวางเท่านั้น สี ฟอนต์ และจำนวนบทความปรับได้ทั้งหมดหลังสร้าง widget
          </p>
        </div>
      )}
    </Modal>
  );
}
